import { useState, useEffect, useContext } from "react";
import { Navigate } from "react-router-dom";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import { Badge, Card, Tooltip } from "@mui/material";
import { Link } from "react-router-dom";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import SuiAvatar from "components/SuiAvatar";
import SuiBadge from "components/SuiBadge";
import NumberFormat from "react-number-format";
import curved9 from "assets/images/upstyled/default_background.png";
import { AccountContext } from "context/account";
import moment from "moment";
import { canResizeImageUrl, getStyleImages, getFileImage } from "utils/imageHelper";

import AddIcon from "@mui/icons-material/Add";
import HeartBrokenIcon from "@mui/icons-material/HeartBroken";
import FavoriteIcon from "@mui/icons-material/Favorite";

import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";

import { RemoveProspectDialog } from "components/UpStyled/project-card/ProjectCard2";

function ProjectCard3({
  project = { services: [] },
  className,
  detailUrl = "#",
  chats = null,
  isRead = false,
  onView = () => {},
  addProspect = () => {},
  removeProspect = null,
}) {
  const [images, setImages] = useState([]);
  const [removeProspectVisible, setRemoveProspectVisible] = useState(false);

  const metadata = project?.metadata ? project.metadata : {}; //project metadata
  const products = project?.products || [];

  const account = useContext(AccountContext);
  let userid = account.id;

  useEffect(() => {
    let styleImages = [];
    //add style first image
    for (let i = 0; i < products.length; i++) {
      let imageFiles = products[i]?.style?.imageFiles || [];
      let techPacksFiles = products[i]?.style?.techPacksFiles || [];

      let temp = [...imageFiles, ...techPacksFiles];
      if (account.account_type == "de") {
        temp = getStyleImages(products[i], false);
      }
      styleImages.push(...temp);
    }
    //add all project reference image
    const fileList = metadata["othersFiles"] || [];
    const files = fileList.filter((f) => f?.type && f.type.includes("image"));
    if (files && files.length > 0) {
      styleImages.push(...files);
    }
    if (styleImages.length == 0) {
      styleImages = [{ url: curved9, name: "deafult_image" }];
    }
    setImages(styleImages);
  }, [products, metadata]);

  const servicesDetail = project?.quote?.services_required || {};
  const serviceRequired = Object.keys(servicesDetail);

  const formatProjectDate = (project) => {
    let prefix = "";
    let date = "";
    if (project.status == "active") {
      prefix = "Project was desired end at: ";
      const dateObj = project?.quote?.desired_end_date || project?.desired_end_date;
      date = dateObj ? moment(dateObj).format("yyyy-MM-DD") : "No Desired Date";
    } else if (project.status == "requested" || project.status == "init") {
      prefix = "Project was desired start at: ";
      const dateObj = project?.quote?.desired_start_date || project?.desired_start_date;
      date = dateObj ? moment(dateObj).format("yyyy-MM-DD") : "No Desired Date";
    } else if (project.status == "completed") {
      prefix = "Project finished at: ";
      const dateObj = project?.metadata?.completed_at || project?.completed_at;
      date =
        project?.metadata?.completed_at || project?.completed_at
          ? moment(dateObj).format("yyyy-MM-DD")
          : "No Date";
    } else if (project.status == "canceled") {
      prefix = "Project canceled at: ";
      const dateObj = project?.metadata?.canceled_at || project?.canceled_at;
      date =
        project?.metadata?.canceled_at || project?.canceled_at
          ? moment(dateObj).format("yyyy-MM-DD")
          : "No Date";
    }

    if (prefix) {
      return (
        <SuiTypography
          variant="body2"
          textTransform="capitalize"
          color="secondary"
          sx={{
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
          }}
        >
          {prefix} {date}
        </SuiTypography>
      );
    } else {
      return (
        <SuiTypography
          variant="body2"
          textTransform="capitalize"
          color="secondary"
          sx={{
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "1",
            WebkitBoxOrient: "vertical",
          }}
        >
          N/A
        </SuiTypography>
      );
    }
  };

  const budgetMin = project?.quote?.budget_min;
  const budgetMax = project?.quote?.budget_max;

  const handleViewProject = () => {
    onView();
    location.assign(detailUrl);
  };

  const users_favorites = project?.users_favorites || [];
  const isMyRequest = project?.audiences && project.audiences.includes(account.id);

  return (
    <Card
      sx={{
        borderColor: ({ borders }) => borders.borderColor,
        boxShadow: ({ boxShadows: { md } }) => md,
        mb: 2,
      }}
    >
      <SuiBox
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        gap={2}
        sx={{ height: "auto" }}
      >
        <SuiBox
          position="relative"
          sx={{ height: "100%", width: "100.25%", maxWidth: 180, minWidth: 180 }}
        >
          <Link to={detailUrl}>
            <ImageCardWrapper
              data={images && images.length > 0 ? images[0] : ""}
              height={"100%"}
              width={180}
              sx={{ overflow: "hidden" }}
            />
          </Link>
        </SuiBox>
        <SuiBox position="relative" sx={{ minWidth: 180, flex: "1 0 auto" }}>
          <SuiBadge
            variant="gradient"
            color={isMyRequest ? "warning" : "info"}
            badgeContent={isMyRequest ? "My Request" : "Public Request"}
            size="md"
            sx={{ mb: 0, orderRadius: 0 }}
          />
          <SuiBox py={0.5} pl={1} pr={4} sx={{ width: "100%" }}>
            <SuiBox display="flex" alignItems="center" justifyContent="flex-start" gap={1}>
              <Tooltip title={project.name}>
                <SuiTypography
                  variant="h6"
                  fontWeight="medium"
                  textTransform="capitalize"
                  lineHeight="1.2"
                  component={Link}
                  to={detailUrl}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: "1",
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {project.name}
                </SuiTypography>
              </Tooltip>
              <SuiBox>
                <SuiBadge
                  variant="contained"
                  color={isRead ? "secondary" : "success"}
                  badgeContent={isRead ? "Read" : "Unread"}
                  size="sm"
                  sx={{ my: 0.5 }}
                />
              </SuiBox>
            </SuiBox>

            <SuiBox display="flex" alignItems="center">
              <SuiBox key={"budget"} display="flex" alignItems="center" minWidth={90}>
                <SuiTypography
                  variant="body1"
                  fontWeight="bold"
                  textTransform="capitalize"
                  mr={0.5}
                >
                  By Designer:
                </SuiTypography>
              </SuiBox>
              <SuiBox>
                <SuiTypography
                  variant="body1"
                  textTransform="none"
                  color="secondary"
                  lineHeight="1.2"
                  sx={{
                    width: "100%",
                    minHeight: "16px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: "1",
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {project?.store_name ? project.store_name : "N/A"}
                </SuiTypography>
              </SuiBox>
            </SuiBox>
            <SuiBox display="flex" alignItems="center">
              <SuiBox key={"description"} display="flex" alignItems="center" sx={{ minWidth: 85 }}>
                <SuiTypography
                  variant="body1"
                  fontWeight="bold"
                  textTransform="capitalize"
                  mr={0.5}
                  sx={{ workbreak: "keep-all", whiteSpace: "nowrap" }}
                >
                  Description:
                </SuiTypography>
              </SuiBox>
              <SuiBox sx={{ flex: "1 1 auto" }}>
                <SuiTypography
                  variant="body1"
                  textTransform="none"
                  color="secondary"
                  lineHeight="1.2"
                  sx={{
                    minHeight: "16px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: "1",
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {project.description
                    ? project.description
                    : "There is no description for this project!"}
                </SuiTypography>
              </SuiBox>
            </SuiBox>
            <SuiBox display="flex" sx={{ flexWrap: "wrap" }}>
              <SuiBox sx={{ minWidth: 125 }}>
                <SuiTypography
                  variant="body1"
                  fontWeight="bold"
                  textTransform="capitalize"
                  mr={0.5}
                  sx={{ workbreak: "keep-all", whiteSpace: "nowrap" }}
                >
                  Service Needed:
                </SuiTypography>
              </SuiBox>
              <SuiBox sx={{ minHeight: 25 }}>
                <SuiBox display="flex" sx={{ flexWrap: "wrap" }}>
                  {serviceRequired.map((s) => (
                    <SuiBadge
                      variant="contained"
                      color="secondary"
                      badgeContent={s}
                      size="sm"
                      sx={{ my: 0.5 }}
                    />
                  ))}
                </SuiBox>
              </SuiBox>
            </SuiBox>
            <SuiBox display="flex" justifyContent="space-between">
              <SuiBox display="flex" sx={{ flexWrap: "wrap", minWidth: 160 }}>
                <SuiBox key={"budget"} display="flex" py={0.5} pr={2} alignItems="center">
                  <SuiTypography
                    variant="body1"
                    fontWeight="bold"
                    textTransform="capitalize"
                    mr={0.5}
                  >
                    Budget:
                  </SuiTypography>
                  {budgetMin != budgetMax ? (
                    <SuiBox display="flex" alignItems="center">
                      <NumberFormat
                        value={budgetMin}
                        displayType={"text"}
                        thousandSeparator={true}
                        prefix={"$"}
                        renderText={(value, props) => (
                          <SuiTypography
                            variant="body1"
                            fontWeight="regular"
                            color="text"
                            {...props}
                          >
                            {value}
                          </SuiTypography>
                        )}
                      />
                      ~{" "}
                      {budgetMax > 0 ? (
                        <NumberFormat
                          value={budgetMax}
                          displayType={"text"}
                          thousandSeparator={true}
                          prefix={"$"}
                          renderText={(value, props) => (
                            <SuiTypography
                              variant="body1"
                              fontWeight="regular"
                              color="text"
                              {...props}
                            >
                              {value}
                            </SuiTypography>
                          )}
                        />
                      ) : null}
                    </SuiBox>
                  ) : (
                    <NumberFormat
                      value={budgetMin}
                      displayType={"text"}
                      thousandSeparator={true}
                      prefix={"$"}
                      renderText={(value, props) => (
                        <SuiTypography variant="body1" fontWeight="regular" color="text" {...props}>
                          {value}
                        </SuiTypography>
                      )}
                    />
                  )}
                </SuiBox>
              </SuiBox>
            </SuiBox>
          </SuiBox>
        </SuiBox>
        <SuiBox position="relative" sx={{ height: "100%", mr: 2, minWidth: 160, width: 160 }}>
          <SuiBox
            display="flex"
            justifyContent="center"
            flexDirection="column"
            alignItems="flex-start"
            gap={1}
          >
            {addProspect && !users_favorites.includes(account.id) ? (
              <Tooltip title="Add to your Saved Interests List!">
                <SuiButton
                  variant="outlined"
                  size="small"
                  color="error"
                  sx={{
                    padding: "0px 8px",
                    fontSize: 12,
                    lineHeight: 0,
                    minHeight: 30,
                    width: "100%",
                    textTransform: "capitalize",
                    borderRadius: "4px",
                    mb: 0.5,
                  }}
                  onClick={addProspect}
                >
                  <FavoriteIcon sx={{ height: 14, width: 14, mr: 0.5 }} />
                  <div>Interest</div>
                </SuiButton>
              </Tooltip>
            ) : null}
            {removeProspect &&
            (!project.users_unfavor || !project.users_unfavor.includes(account.id)) ? (
              <SuiButton
                variant="outlined"
                size="small"
                color="dark"
                sx={{
                  padding: "0px 8px",
                  fontSize: 12,
                  lineHeight: 0,
                  minHeight: 30,
                  width: "100%",
                  textTransform: "capitalize",
                  borderRadius: "4px",
                  mb: 0.5,
                }}
                onClick={() => setRemoveProspectVisible(true)}
              >
                <HeartBrokenIcon sx={{ height: 14, width: 14, mr: 0.5 }} />
                Not interested
              </SuiButton>
            ) : null}

            <SuiButton
              variant="outlined"
              size="small"
              color="info"
              sx={{
                padding: "0px 8px",
                fontSize: 12,
                lineHeight: 0,
                minHeight: 30,
                width: "100%",
                textTransform: "capitalize",
                borderRadius: "4px",
                mb: 0.5,
              }}
              onClick={handleViewProject}
            >
              View
            </SuiButton>
            <SuiBox display="flex" alignItems="center" flexGrow={0}>
              {formatProjectDate(project)}
            </SuiBox>
          </SuiBox>
        </SuiBox>
      </SuiBox>

      {removeProspect && removeProspectVisible ? (
        <RemoveProspectDialog
          project={project}
          userid={account.id}
          option={{ fullWidth: true, maxWidth: "sm" }}
          visible={removeProspectVisible}
          okColor="error"
          okText="Remove"
          handleOk={(data) => {
            removeProspect(data);
            setRemoveProspectVisible(false);
          }}
          handleClose={() => setRemoveProspectVisible(false)}
        />
      ) : null}
    </Card>
  );
}

export default ProjectCard3;
